/**
 * Parsed `/view/:t/:id?mode=&post=` params — the stable route contract for the view screen
 * (§12.1). Lives in the shell so the view feature can be rewritten without touching URL handling.
 */

import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import type { SubjectType, ViewMode } from '@lw/shared';
import {
  DEFAULT_VIEW_MODE,
  POST_PREVIEW_PARAM,
  VIEW_MODE_PARAM,
  parsePostParam,
  parseSubjectId,
  parseSubjectType,
  parseViewMode,
} from './routes';

export type ViewRoute = {
  /** `null` when the URL segment is not `profile`/`group` or the id is not a positive integer. */
  subjectType: SubjectType | null;
  id: number | null;
  mode: ViewMode;
  setMode: (mode: ViewMode) => void;
  /** Post whose preview popup is open; `null` when `?post=` is absent or unparseable. */
  previewPostId: number | null;
  /** Opening pushes (Back closes the popup); every other move replaces. */
  setPreviewPostId: (postId: number | null, opts?: { push?: boolean }) => void;
  /** Pops the entry a `{ push: true }` open added, so closing leaves no dead history step. */
  historyBack: () => void;
};

export function useViewRoute(): ViewRoute {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const subjectType = useMemo(() => parseSubjectType(params.t), [params.t]);
  const id = useMemo(() => parseSubjectId(params.id), [params.id]);
  const mode = parseViewMode(searchParams.get(VIEW_MODE_PARAM));
  const previewPostId = parsePostParam(searchParams.get(POST_PREVIEW_PARAM));

  const setMode = useCallback(
    (next: ViewMode) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (next === DEFAULT_VIEW_MODE) updated.delete(VIEW_MODE_PARAM);
          else updated.set(VIEW_MODE_PARAM, next);
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Functional updates so the two params never clobber each other: a mode switch keeps an open
  // popup, and closing the popup keeps the mode.
  const setPreviewPostId = useCallback(
    (postId: number | null, opts?: { push?: boolean }) => {
      setSearchParams(
        (previous) => {
          const updated = new URLSearchParams(previous);
          if (postId === null) updated.delete(POST_PREVIEW_PARAM);
          else updated.set(POST_PREVIEW_PARAM, String(postId));
          return updated;
        },
        { replace: opts?.push !== true },
      );
    },
    [setSearchParams],
  );

  const historyBack = useCallback(() => navigate(-1), [navigate]);

  return { subjectType, id, mode, setMode, previewPostId, setPreviewPostId, historyBack };
}
